import {AfterViewInit, Component, EventEmitter, HostBinding, Input, OnInit, Output} from '@angular/core';
import {ApiClientService} from '../../../../services/api-client.service';
import {EmailService} from '../../../../services/email.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {NotificationsApiService} from '../../../../services/api/notifications-api.service';
import {catchError, distinctUntilChanged, filter, map, repeatWhen, skip, switchMap, tap} from 'rxjs/operators';
import {Subject} from 'rxjs/Subject';
import {UserService} from '../../../admin/user/services/user.service';
import {emailRegex} from '../../../../modules/utils/validators/LocalValidators';
import {DocumentsService} from '../../../../services/documents.service';
import {of} from 'rxjs/observable/of';

declare const $: any;
declare const toastr: any;
declare const swal: any;

@Component({
    selector: 'notification-details-step',
    templateUrl: './notification-details-step.component.html',
    styleUrls: ['./notification-details-step.component.scss']
})
export class NotificationDetailsStepComponent implements OnInit, AfterViewInit {
    @Input() files: any[];
    @Input() isShowPreloader: boolean;
    @Output() nextStep = new EventEmitter();

    @HostBinding('class.sk-loading')
    get isLoading() {
        return this.isShowPreloader;
    }

    message = `<p>Hi,</p>
<p>A new file is uploaded to Max Condo Club</p>
<p>[[FILE_NAME]]</p>
<p>
    To view the file please <a href="http://web.maxcondoclub.com" target="_blank">login</a>
</p>
<p>
    Thank you.
</p>
    `;
    availableEmails = [];

    form: FormGroup;
    private placeholders = [
        // '[[SUITE_NUMBER]]',
        '[[RESIDENT_NAME]]',
        '[[RESIDENT_EMAIL]]',
        // '[[RESIDENT_MOBILE_PHONE]]',
        // '[[RESIDENT_HOME_PHONE]]',
        // '[[RESIDENT_BUSINESS_PHONE]]',
        // '[[BUILDING_NAME]]',
        // '[[BUILDING_ADDRESS]]',
        '[[FILE_NAME]]',
        '[[FILE_LINK]]',
    ];
    formErrorTexts = {
        senderEmail: {
            required: 'Sender email is required',
            pattern: 'Invalid sender email format',
            not_confirmed: 'Provided sender email is not confirmed',
        },
        message: {
            required: 'Message is required'
        },
        subject: {
            required: 'Subject is required'
        }
    };
    private currentUser: any;

    constructor(
        private api: ApiClientService,
        private emailService: EmailService,
        private notificationsApiService: NotificationsApiService,
        private documentsService: DocumentsService,
        private userService: UserService
    ) {
        this.currentUser = this.userService.account.user;
        this.form = new FormGroup({
            senderEmail: new FormControl('', Validators.compose([
                    Validators.required,
                    Validators.pattern(emailRegex)
                ]),
            ),
            subject: new FormControl('File uploaded to Max Condo Club', Validators.required),
            message: new FormControl(this.message, Validators.compose([Validators.required])),
            saveAsDefault: new FormControl(false)
        });
        this.form.get('senderEmail')
            .valueChanges
            .pipe(
                skip(1),
                distinctUntilChanged(),
                filter(v => emailRegex.test(v)),
                switchMap(value => this.checkEmail(value)),
                tap(asyncErrors => {
                    const senderEmailControl = this.form.get('senderEmail');
                    const errors = {
                        ...(senderEmailControl.errors || {}),
                        ...(asyncErrors || {})
                    };
                    this.form.get('senderEmail').setErrors(Object.keys(errors).length > 0 ? errors : null);
                }),
            )
            .subscribe();
        this.setInitialSenderEmail();
    }

    initSummernote() {
        const summernote = $('#summernote');
        summernote.summernote({
            dialogsInBody: true,
            callbacks: {
                onChange: contents => {
                    this.message = contents;
                    this.form.patchValue({message: contents});
                }
            },
            toolbar: [
                ['style', ['style']],
                ['font', ['bold', 'italic', 'underline', 'clear']],
                ['fontname', ['fontname']],
                ['color', ['color']],
                ['para', ['ul', 'ol', 'paragraph']],
                ['table', ['table']],
                ['insert', ['link', 'hr']],
                ['help', ['help']]
            ],
            fontNames: ['Arial', 'open sans', 'Courier New'],
            disableDragAndDrop: true
        });
        $('.note-btn-bold').removeClass('active');

        const btn = `
            <div class="input-group-btn email-placeholders-btn">
                <button data-toggle="dropdown" class="btn btn-sm btn-white dropdown-toggle"
                        type="button" aria-expanded="false">PL <span class="caret"></span></button>
                <ul class="dropdown-menu pull-right">
                    ${this.getPlaceholders()}
                </ul>
            </div>
        `;

        $(btn).insertAfter('[data-original-title="Help"]')
            .on('click', 'li a', e => {
                summernote.summernote('editor.restoreRange');
                summernote.summernote('editor.focus');
                summernote.summernote('editor.insertText', e.target.innerText);
            });
        $('.note-editable').html(this.message).on('focusout', e => {
            if ($(e.relatedTarget).closest('.note-toolbar').length) {
                summernote.summernote('editor.saveRange');
            }
        });
    }

    ngOnInit() {
    }

    ngAfterViewInit(): void {
        this.setMessageAndSubject()
            .pipe(
                tap(() => this.initSummernote())
            )
            .subscribe();
    }

    submit() {
        if (this.form.valid) {
            const formValue = this.form.value;
            const data = this.files.map(fileInfo => ({
                ...fileInfo,
                ...formValue
            }));
            this.nextStep.emit(data);
        } else {
            const errors = this.getFormErrors();
            for (const error of errors) {
                toastr.warning(error);
            }
            return;
        }
    }

    checkEmail(value: string) {
        const retryCheck = new Subject();
        return this.notificationsApiService
            .checkSender(value)
            .pipe(
                repeatWhen(() => retryCheck),
                map((body) => {
                    if (body.status == 'Success') {
                        toastr.warning('Changing "From" field may result in some emails being sent to spam box.');
                        return null;
                    } else {
                        toastr.error('The entered email is not verified');
                        swal({
                            title: 'Email is not verified',
                            text: 'We will send confirmation email to your inbox. Please click Yes to proceed, or No if you want to change email',
                            type: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#DD6B55',
                            confirmButtonText: 'Yes',
                            cancelButtonText: 'No',
                            closeOnConfirm: true
                        }, () => this.notificationsApiService.addSender(value)
                            .pipe(
                                tap(
                                    () => {
                                        swal({
                                            title: 'Verification Email sent',
                                            text: 'If you have confirmed your new email address click "Check again"',
                                            type: 'warning',
                                            confirmButtonText: 'Check again',
                                            cancelButtonText: 'Close',
                                        }, () => retryCheck.next());
                                    }
                                )
                            )
                            .subscribe());
                        return {
                            not_confirmed: true
                        };
                    }
                })
            );
    }

    private getPlaceholders() {
        return this.placeholders.map(item => `<li><a>${item}</a></li>`).join('');
    }

    private getFormErrors() {
        return Object.keys(this.form.controls).reduce((formErrors, fieldName) => {
            return [
                ...formErrors,
                ...Object.keys(this.form.controls[fieldName].errors || {})
                    .map(errorName => this.formErrorTexts[fieldName][errorName])
            ];
        }, []);
    }

    private async setInitialSenderEmail() {
        let companySettings = null;
        if (this.currentUser.company) {
            companySettings = await this.emailService.getCompanySettings(this.currentUser.company.id).toPromise();
        }
        const corporationSettings = await this.emailService.getCorporationSettings().toPromise();
        const availableEmails = [];
        if (companySettings && companySettings.emailSender) {
            availableEmails.push(companySettings.emailSender);
        }
        corporationSettings.entities.filter(entity => !!entity.emailSender).forEach(entity => availableEmails.push(entity.emailSender));
        availableEmails.push('notice@maxcondoclub.ca');
        this.availableEmails = availableEmails;
        this.form.patchValue({
            senderEmail: availableEmails[0]
        });
    }

    private setMessageAndSubject() {
        const currentDirectoryId = this.documentsService.currentDirectoryId.getValue();
        if (currentDirectoryId == 1) {
            return of({message: '', subject: ''});
        }
        return this.documentsService.getDocument(currentDirectoryId)
            .pipe(
                tap(({message, subject}) => {
                    if (message) {
                        this.message = message;
                    }
                    if (subject) {
                        this.form.patchValue({subject});
                    }
                }),
                catchError(err => {
                    this.documentsService.api.errorHandler(err);
                    return of({message: '', subject: ''});
                }),
            );
    }

    setSender(email: string) {
        this.form.get('senderEmail').patchValue(email);
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/documents/upload-file-modal/notification-details-step/notification-details-step.component.ts